import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
import RefreshButton from "../../../Component/Common/RefreshButton";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import { BASE_URL } from "../../../config/api";
import SimpleTable from "../../../Component/Common/SimpleTable";
import ViewButton from "../../../Component/Common/ViewButton";
import SearchInput from "../../../Component/Common/SearchInput";

/* ---------------- STATUS PILL ---------------- */
function StatusPill({ active }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] sm:text-xs font-semibold " +
        (active
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-rose-50 text-rose-700 border-rose-200")
      }
    >
      <span
        className={
          "h-1.5 w-1.5 rounded-full " +
          (active ? "bg-emerald-500" : "bg-rose-500")
        }
      />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

export default function AgentsView({entityType = null,
  entityId = null,}) {
  const navigate = useNavigate();

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
const sessionRole = Number(authUser?.role);
const sessionId = authUser?.id;


  /* ---------------- SEARCH DEBOUNCE ---------------- */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const buildParams = (page) => {
  const params = {
    role: 4, // agent
    page,
  };

  // ✅ add search ONLY when user types
  if (debouncedSearch) {
    params.search_value = debouncedSearch;
  }

  /* ---------------- ENTITY OVERRIDE (UserDetails) ---------------- */
  if (entityType && entityId) {
    if (entityType === "company") {
      params.company_id = entityId;
    }
    return params; // 🔥 entityType wins
  }

  /* ---------------- SESSION-BASED LOGIC ---------------- */
  if (sessionRole === 2) {
    params.company_id = sessionId;
  }

  // role === 1 → admin → no extra filters
  return params;
};

  /* ---------------- API ---------------- */
  const fetchAgents = async (page = 1) => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}common/get-users`, {
        params: buildParams(page),
      });


      setAgents(res.data?.data || []);
      setCurrentPage(res.data?.pagination?.current_page || 1);
      setTotalPages(res.data?.pagination?.last_page || 1);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load agents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, entityType, entityId]);

  /* ---------------- FILTER (CLIENT SIDE) ---------------- */
  // const filteredAgents = useMemo(() => {
  //   if (!debouncedSearch) return agents;
  //   const q = debouncedSearch.toLowerCase();

  //   return agents.filter((a) =>
  //     [
  //       a.id,
  //       a.business_name,
  //       a.contact_person,
  //       a.contact_phone,
  //       a.contact_email,
  //     ]
  //       .join(" ")
  //       .toLowerCase()
  //       .includes(q)
  //   );
  // }, [agents, debouncedSearch]);

  const filteredAgents = agents;

  /* ---------------- TABLE ROWS ---------------- */
  const agentRows = filteredAgents.map((a) => ({
    raw: a,
    cells: [
      {
        title: a.business_name ? `${a.business_name} (${a.company_code || "-"})` : a.contact_person || "—",
        sub: `ID: ${a.id}`,
      },
      {
        title: `${a.city || "—"}, ${a.state || "—"}`,
      },
      {
        title: a.contact_phone || "—",
        sub: a.contact_email || "—",
      },
      {
        title: a.created_by_name || "—",
        // sub: a.contact_email || "—",
      },
      {
        title: <StatusPill active={a.status === 1} />,
      },
    ],
  }));

  return (
    <div className="space-y-4">
      <SectionCard title="Agent Performance & Commission">
        {/* SEARCH + REFRESH (below heading) */}
        <div className="flex gap-2 mb-4">
          <SearchInput
            value={search}
            onChange={setSearch}
            onClear={() => setSearch("")}
            placeholder="Search by name, phone, email…"
            size="md"
            className="flex-1 "
          />
          <div className="hidden lg:block">
          <RefreshButton onClick={() => fetchAgents(currentPage)} />
          </div>
        </div>

        {/* Loader */}
        {loading && (
          <div className="flex justify-center items-center h-[50vh]">
            <RegularLoader />
          </div>
        )}

        {/* Empty */}
        {!loading && filteredAgents.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
              <Search className="h-5 w-5" />
            </div>
            <div className="text-sm font-semibold text-slate-900">
              No agents found
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Try changing your search keywords.
            </div>
          </div>
        )}

        {/* Table */}
        {!loading && filteredAgents.length > 0 && (
          <SimpleTable
            headers={["Agent", "Region", "Contact","Created By", "Status"]}
            rows={agentRows}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            renderActions={(a) => (
              <ViewButton
                onClick={() =>
                  navigate(`/user/${a.id}?type=agent`, {
                    state: { entityType: "agent", data: a },
                  })
                }
              />
            )}
            showSrNo
            pageSize={10}
          />
        )}
      </SectionCard>

    </div>
  );
}



// import { Search, Eye } from "lucide-react";
// import { useEffect, useMemo, useState } from "react";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import toast from "react-hot-toast";

// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import Pagination from "../../../Component/Common/Pagination";
// import { BASE_URL } from "../../../config/api";
// import SearchInput from "../../../Component/Common/SearchInput";

// // Status pill (same UX as CompanyView)
// function StatusPill({ active }) {
//   return (
//     <span
//       className={
//         "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] sm:text-xs font-semibold " +
//         (active
//           ? "bg-emerald-50 text-emerald-700 border-emerald-200"
//           : "bg-rose-50 text-rose-700 border-rose-200")
//       }
//     >
//       <span
//         className={
//           "h-1.5 w-1.5 rounded-full " +
//           (active ? "bg-emerald-500" : "bg-rose-500")
//         }
//       />
//       {active ? "Active" : "Inactive"}
//     </span>
//   );
// }

// export default function AgentsView() {
//   const navigate = useNavigate();

//   const [agents, setAgents] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [search, setSearch] = useState("");
//   const [debouncedSearch, setDebouncedSearch] = useState("");

//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);

//   // 🔍 debounce search
//   useEffect(() => {
//     const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
//     return () => clearTimeout(t);
//   }, [search]);

//   const fetchAgents = async (page = 1) => {
//   try {
//     setLoading(true);

//     const res = await axios.get(
//       `${BASE_URL}common/get-users`,
//       {
//         params: {
//           role: 4,
//           page,
//           search_value: debouncedSearch || "",
//         },
//       }
//     );

//     // ✅ CORRECT RESPONSE MAPPING
//     setAgents(res.data?.data || []);

//     setCurrentPage(res.data?.pagination?.current_page || 1);
//     setTotalPages(res.data?.pagination?.last_page || 1);

//   } catch (e) {
//     toast.error("Failed to load agents");
//   } finally {
//     setLoading(false);
//   }
// };


//   useEffect(() => {
//     fetchAgents(currentPage);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [currentPage, debouncedSearch]);

//   return (
//     <div className="space-y-4">
//       <SectionCard
//         title="Agent Performance & Commission"
//         right={
//           <div className="flex items-stretch gap-2">
//             {/* Search */}
//             <SearchInput
//               value={search}
//               size="md"
//               placeholder="Search by name, phone, email…"
//               onChange={(val) => {
//                 setSearch(val);
//                 setCurrentPage(1);
//               }}
//               onClear={() => {
//                 setSearch("");
//                 setCurrentPage(1);
//               }}
//             />


//             <RefreshButton onClick={() => fetchAgents(currentPage)} />
//           </div>
//         }
//       >
//         {/* Loader */}
//         {loading && (
//           <div className="flex justify-center items-center h-[50vh]">
//             <RegularLoader />
//           </div>
//         )}

//         {/* Empty state */}
//         {!loading && agents.length === 0 && (
//           <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
//             <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
//               <Search className="h-5 w-5" />
//             </div>
//             <div className="text-sm font-semibold text-slate-900">
//               No agents found
//             </div>
//             <div className="text-xs text-slate-500 mt-1">
//               Try changing your search keywords.
//             </div>
//           </div>
//         )}

//         {/* Table */}
//         {!loading && agents.length > 0 && (
//           <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
//             <div className="overflow-x-auto">
//               <table className="min-w-full text-left">
//                 <thead className="bg-slate-50">
//                   <tr className="text-xs font-semibold text-slate-600">
//                     <th className="px-4 py-3">Agent</th>
//                     <th className="px-4 py-3">Region</th>
//                     <th className="px-4 py-3">Contact</th>
//                     <th className="px-4 py-3">Status</th>
//                     <th className="px-4 py-3 text-right">Actions</th>
//                   </tr>
//                 </thead>

//                 <tbody className="divide-y divide-slate-100">
//                   {agents.map((a) => (
//                     <tr
//                       key={a.id}
//                       className="hover:bg-blue-50/40 transition-colors"
//                     >
//                       {/* Agent */}
//                       <td className="px-4 py-3">
//                         <div className="text-sm font-semibold text-slate-900">
//                           {a.business_name || a.contact_person}
//                         </div>
//                         <div className="text-xs text-slate-500">
//                           ID: {a.id}
//                         </div>
//                       </td>

//                       {/* Region */}
//                       <td className="px-4 py-3 text-sm text-slate-700">
//                         {a.city || "—"}, {a.state || "—"}
//                       </td>

//                       {/* Contact */}
//                       <td className="px-4 py-3 text-sm text-slate-700">
//                         <div>{a.contact_phone || "—"}</div>
//                         <div className="text-xs text-slate-500">
//                           {a.contact_email || "—"}
//                         </div>
//                       </td>

//                       {/* Status */}
//                       <td className="px-4 py-3">
//                         <StatusPill active={a.status === 1} />
//                       </td>

//                       {/* Actions */}
//                       <td className="px-4 py-3 text-right">
//                         <button
//                           type="button"
//                           onClick={() =>
//                             navigate(`/user/${a.id}?type=agent`, {
//                               state: {
//                                 entityType: "agent",
//                                 data: a,
//                               },
//                             })
//                           }
//                           className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"
//                         >
//                           <Eye className="h-4 w-4" />
//                           View Details
//                         </button>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>

//             {/* Footer */}
//             <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-slate-200 px-4 py-3">
//               <div className="text-xs text-slate-500">
//                 Showing{" "}
//                 <span className="font-semibold text-slate-700">
//                   {agents.length}
//                 </span>{" "}
//                 agents
//               </div>

//               {totalPages > 1 && (
//                 <Pagination
//                   currentPage={currentPage}
//                   totalPages={totalPages}
//                   onPageChange={setCurrentPage}
//                 />
//               )}
//             </div>
//           </div>
//         )}
//       </SectionCard>
//     </div>
//   );
// }




// import { Plus, Search } from "lucide-react";
// import { useEffect, useState } from "react";
// import axios from "axios";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import BlueButton from "../../../Component/Common/BlueButton";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { useNavigate } from "react-router-dom";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import Pagination from "../../../Component/Common/Pagination";
// import { BASE_URL } from "../../../config/api";

// export default function AgentsView() {

//   const navigate = useNavigate();
//   const [agents, setAgents] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);

//   // 🔍 Search input state (debounced)
//   const [search, setSearch] = useState("");
//   const [debouncedSearch, setDebouncedSearch] = useState("");


//   // ⏳ Debounce search input 
//   useEffect(() => {
//     const timer = setTimeout(() => setDebouncedSearch(search), 400);
//     return () => clearTimeout(timer);
//   }, [search]);


//   // 🚀 Fetch Agents from API
//   const fetchAgents = async () => {
//     try {
//       setLoading(true);

//       const response = await axios.get(
//         `${BASE_URL}common/get-users?role=4&page=${currentPage}&search=${debouncedSearch || ""}`
//       );

//       const apiData = response.data?.data || {};

//       setAgents(apiData.data || []);
//       setTotalPages(apiData.last_page || 1);

//     } catch (err) {
//       console.error("Error fetching agents:", err);
//     } finally {
//       setLoading(false);
//     }
//   };


//   // 🔁 Fetch whenever page or search changes
//   useEffect(() => {
//     fetchAgents();
//   }, [currentPage, debouncedSearch]);



//   return (
//     <div className="space-y-4">
//       <SectionCard title="Agent Performance & Commission">

//         {/* 🔍 Search + Button Group */}
//         <div className="flex justify-end gap-2 items-center mb-2">
          
//           {/* Search UI */}
//           <div className="flex items-center gap-1 px-3 py-1 rounded-xl border border-slate-200 bg-slate-50">
//             <Search className="w-4 h-4 text-slate-800" />
//             <input
//               type="text"
//               placeholder="Search agents..."
//               className="bg-transparent text-xs outline-none w-auto placeholder:text-slate-700"
//               value={search}
//               onChange={(e) => {
//                 setSearch(e.target.value);
//                 setCurrentPage(1);
//               }}
//             />
//           </div>

//           <RefreshButton onClick={fetchAgents} />
//         </div>

//         {/* Loader */}
//         {loading && (
//           <div className="flex justify-center items-center h-[50vh]">
//             <RegularLoader />
//           </div>
//         )}

//         {/* No Records */}
//         {!loading && agents.length === 0 && (
//           <p className="text-center text-sm text-slate-500 py-3">
//             No matching agents found.
//           </p>
//         )}

//         {/* Table */}
//         {!loading && agents.length > 0 && (
//           <>
//             <div className="overflow-x-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-blue-300">
//               <table className="min-w-full text-[10px] sm:text-sm text-center">
//                 <thead>
//                   <tr className="border-b border-slate-100 text-slate-500 px-2">
//                     <th className="py-2 px-2">Agent</th>
//                     <th className="py-2 px-2">Region</th>
//                     <th className="py-2 px-2">Contact</th>
//                     <th className="py-2 px-2">Retailers</th>
//                     <th className="py-2 px-2">Commission</th>
//                     <th className="py-2 px-2">Target</th>
//                     <th className="py-2 px-2">Status</th>
//                     <th className="py-2 px-2">Actions</th>
//                   </tr>
//                 </thead>

//                 <tbody className="px-2">
//                   {agents.map((a) => (
//                     <tr key={a.id} className="border-b border-slate-50 hover:bg-blue-50/60">

//                       {/* Agent */}
//                       <td className="py-2 px-2">
//                         <div className="font-semibold">{a.business_name || a.contact_person}</div>
//                         <div className="text-[9px] text-slate-500">ID: {a.id}</div>
//                       </td>

//                       {/* Region */}
//                       <td className="py-2 px-2">{a.city}, {a.state}</td>

//                       {/* Contact */}
//                       <td className="py-2 px-2">
//                         <div>{a.contact_phone}</div>
//                         <div className="text-[9px] text-slate-500">{a.contact_email}</div>
//                       </td>

//                       {/* Retailers */}
//                       <td className="py-2 px-2">—</td>

//                       {/* Commission */}
//                       <td className="py-2 px-2">—</td>

//                       {/* Target */}
//                       <td className="py-2 px-2">—</td>

//                       {/* Status */}
//                       <td className="py-2 px-2">
//                         <span className={`px-2 py-1 rounded text-[9px] ${
//                           a.status === 1 
//                           ? "bg-green-50 text-green-700 border border-green-200"
//                           : "bg-red-50 text-red-700 border border-red-200"
//                         }`}>
//                           {a.status === 1 ? "Active" : "Inactive"}
//                         </span>
//                       </td>

//                       {/* Actions */}
//                       <td className="py-2 px-2">
//                         <button className="text-[9px] px-2 py-1 border rounded bg-blue-50 text-blue-700 hover:bg-blue-100">
//                           View details
//                         </button>
//                       </td>

//                     </tr>
//                   ))}
//                 </tbody>

//               </table>
//             </div>

//             {/* Pagination Component */}
//             {totalPages > 1 && (
//               <Pagination
//                 currentPage={currentPage}
//                 totalPages={totalPages}
//                 onPageChange={setCurrentPage}
//               />
//             )}
//           </>
//         )}

//       </SectionCard>
//     </div>
//   );
// }




// import { Plus, Search } from "lucide-react";
// import { useEffect, useState } from "react";
// import axios from "axios";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import BlueButton from "../../../Component/Common/BlueButton";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { useNavigate } from "react-router-dom";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import Pagination from "../../../Component/Common/Pagination";

// export default function AgentsView() {

//   const navigate = useNavigate();
//   const [agents, setAgents] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);

//   // 🔍 Search input state (debounced)
//   const [search, setSearch] = useState("");
//   const [debouncedSearch, setDebouncedSearch] = useState("");

//   // 🔹 Get company_id from session
//   const userData = JSON.parse(localStorage.getItem("authUserData"));
//   const companyId = userData?.id;

//   // ⏳ Debounce search input (wait 400ms before calling API)
//   useEffect(() => {
//     const timer = setTimeout(() => setDebouncedSearch(search), 400);
//     return () => clearTimeout(timer);
//   }, [search]);


//   const fetchAgents = async () => {
//     if (!companyId) return;

//     try {
//       setLoading(true);

//       const response = await axios.post(
//         "${BASE_URL}/agent/all",
//         { company_id: companyId, search: debouncedSearch, page: currentPage },
//         { headers: { "Content-Type": "application/json" } }
//       );

//       if (response.data?.status) {
//         setAgents(response.data.data);
//         setTotalPages(response.data?.meta?.last_page || 1);
//       }
//     } catch (err) {
//       console.error("Error fetching agents:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // 🔁 Fetch when page or search input updates
//   useEffect(() => {
//     fetchAgents();
//   }, [currentPage, debouncedSearch]);


//   return (
//     <div className="space-y-4">
//       <SectionCard title="Agent Performance & Commission">

//         {/* 🔍 Search + Button Group */}
//         <div className="flex justify-end gap-2 items-center mb-2">
          
//           {/* Search UI */}
//           <div className="flex items-center gap-1 px-3 py-1 rounded-xl border border-slate-200 bg-slate-50">
//             <Search className="w-4 h-4 text-slate-800" />
//             <input
//               type="text"
//               placeholder="Search agents..."
//               className="bg-transparent text-xs outline-none w-auto placeholder:text-slate-700"
//               value={search}
//               onChange={(e) => {
//                 setSearch(e.target.value);
//                 setCurrentPage(1);
//               }}
//             />
//           </div>

//           <div className="flex gap-2">
//             {/* <BlueButton
//               onClick={() => navigate("/add-entity/agent")} // or "company", "retailer"
//               className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
//               style={{ backgroundColor: "#0B5ED7" }}
//             >
//               <Plus size={14} className="mr-1" /> Add
//             </BlueButton> */}


//             <RefreshButton onClick={fetchAgents} />
//           </div>
//         </div>

//         {/* Loader */}
//         {loading && (
//           <div className="flex justify-center items-center h-[50vh]">
//             <RegularLoader />
//           </div>
//         )}

//         {/* No Records */}
//         {!loading && agents.length === 0 && (
//           <p className="text-center text-sm text-slate-500 py-3">
//             No matching agents found.
//           </p>
//         )}

//         {/* Table */}
//         {!loading && agents.length > 0 && (
//           <>
//             <div className="overflow-x-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-blue-300">
//               <table className="min-w-full text-[10px] sm:text-sm text-center">
//                 <thead>
//                   <tr className="border-b border-slate-100 text-slate-500 px-2">
//                     <th className="py-2 px-2 whitespace-nowrap text-[11px] sm:text-sm">Agent</th>
//                     <th className="py-2 px-2 whitespace-nowrap text-[11px] sm:text-sm">Region</th>
//                     <th className="py-2 px-2 whitespace-nowrap text-[11px] sm:text-sm">Contact</th>
//                     <th className="py-2 px-2 whitespace-nowrap text-[11px] sm:text-sm">Retailers</th>
//                     <th className="py-2 px-2 whitespace-nowrap text-[11px] sm:text-sm">Commission</th>
//                     <th className="py-2 px-2 whitespace-nowrap text-[11px] sm:text-sm">Target</th>
//                     <th className="py-2 px-2 whitespace-nowrap text-[11px] sm:text-sm">Status</th>
//                     <th className="py-2 px-2 whitespace-nowrap text-[11px] sm:text-sm">Actions</th>
//                   </tr>
//                 </thead>

//                 <tbody className="px-2">
//                   {agents.map((a) => (
//                     <tr key={a.id} className="border-b border-slate-50 hover:bg-blue-50/60">

//                       {/* Agent */}
//                       <td className="py-2 px-2 text-[10px] sm:text-xs whitespace-nowrap">
//                         <div className="font-semibold">{a.agent_name}</div>
//                         <div className="text-[9px] text-slate-500">{a.agent_code}</div>
//                       </td>

//                       {/* Region */}
//                       <td className="py-2 px-2 text-[10px] sm:text-xs whitespace-nowrap">
//                         {a.city}, {a.state}
//                       </td>

//                       {/* Contact */}
//                       <td className="py-2 px-2 text-[10px] sm:text-xs whitespace-nowrap">
//                         <div>{a.phone}</div>
//                         <div className="text-[9px] text-slate-500">{a.email}</div>
//                       </td>

//                       {/* Retailer */}
//                       <td className="py-2 px-2 text-[10px] sm:text-xs whitespace-nowrap">
//                         <div>Onboarded: <strong>18</strong></div>
//                         <div className="text-[9px] text-slate-500">Active: 14</div>
//                       </td>

//                       {/* Commission */}
//                       <td className="py-2 px-2 text-[10px] sm:text-xs whitespace-nowrap">
//                         <div>Total: <strong>₹86,500</strong></div>
//                         <div className="text-[9px] text-slate-500">Pending: ₹12,500</div>
//                       </td>

//                       {/* Target */}
//                       <td className="py-2 px-2 text-[10px] sm:text-xs whitespace-nowrap">
//                         <div className="flex items-center justify-between text-[9px] mb-1 sm:text-[10px]">
//                           <span>Retailer target</span>
//                           <span>18/20 (90%)</span>
//                         </div>

//                         <div className="w-full h-2 rounded-full bg-slate-200">
//                           <div className="h-2 bg-blue-600 rounded-full" style={{ width: "90%" }}></div>
//                         </div>
//                       </td>

//                       {/* Status */}
//                       <td className="py-2 px-2 whitespace-nowrap">
//                         <span className={`px-2 py-1 rounded text-[9px] sm:text-[10px] ${
//                           a.status === 1 
//                           ? "bg-green-50 text-green-700 border border-green-200"
//                           : "bg-red-50 text-red-700 border border-red-200"
//                         }`}>
//                           {a.status === 1 ? "Active" : "Inactive"}
//                         </span>
//                       </td>

//                       {/* Actions (Mobile stacked) */}
//                       <td className="py-2 px-2 flex flex-col sm:flex-row gap-1 justify-center min-w-[120px]">
//                         <button className="text-[9px] sm:text-xs px-2 py-1 border border-blue-200 text-blue-700 bg-blue-50 rounded hover:bg-blue-100">
//                           View details
//                         </button>

//                         <button className="text-[9px] sm:text-xs px-2 py-1 border border-slate-200 text-slate-700 bg-slate-50 rounded hover:bg-slate-100">
//                           Download
//                         </button>
//                       </td>

//                     </tr>
//                   ))}
//                 </tbody>

//               </table>
//             </div>

//             {/* Pagination */}
//             {totalPages > 1 && (
//               <Pagination
//                 currentPage={currentPage}
//                 totalPages={totalPages}
//                 onPageChange={setCurrentPage}
//               />
//             )}
//           </>
//         )}

//       </SectionCard>
//     </div>
//   );
// }



// import { Plus, Search } from "lucide-react";
// import { useEffect, useState } from "react";
// import axios from "axios";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import BlueButton from "../../../Component/Common/BlueButton";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { useNavigate } from "react-router-dom";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import Pagination from "../../../Component/Common/Pagination";

// export default function AgentsView() {

//   const navigate = useNavigate();
//   const [agents, setAgents] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);

//   // 🔍 Search input state (debounced)
//   const [search, setSearch] = useState("");
//   const [debouncedSearch, setDebouncedSearch] = useState("");

//   // 🔹 Get company_id from session
//   const userData = JSON.parse(localStorage.getItem("authUserData"));
//   const companyId = userData?.id;

//   // ⏳ Debounce search input (wait 400ms before calling API)
//   useEffect(() => {
//     const timer = setTimeout(() => setDebouncedSearch(search), 400);
//     return () => clearTimeout(timer);
//   }, [search]);


//   const fetchAgents = async () => {
//     if (!companyId) return;

//     try {
//       setLoading(true);

//       const response = await axios.post(
//         "${BASE_URL}/agent/all",
//         { company_id: companyId, search: debouncedSearch, page: currentPage },
//         { headers: { "Content-Type": "application/json" } }
//       );

//       if (response.data?.status) {
//         setAgents(response.data.data);
//         setTotalPages(response.data?.meta?.last_page || 1);
//       }
//     } catch (err) {
//       console.error("Error fetching agents:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // 🔁 Fetch when page or search input updates
//   useEffect(() => {
//     fetchAgents();
//   }, [currentPage, debouncedSearch]);


//   return (
//     <div className="space-y-4">
//       <SectionCard title="Agent Performance & Commission">

//         {/* 🔍 Search + Button Group */}
//         <div className="flex justify-end gap-2 items-center mb-2">
          
//           {/* Search UI */}
//           <div className="flex items-center gap-1 px-3 py-1 rounded-xl border border-slate-200 bg-slate-50">
//             <Search className="w-4 h-4 text-slate-800" />
//             <input
//               type="text"
//               placeholder="Search agents..."
//               className="bg-transparent text-xs outline-none w-40 placeholder:text-slate-700"
//               value={search}
//               onChange={(e) => {
//                 setSearch(e.target.value);
//                 setCurrentPage(1);
//               }}
//             />
//           </div>

//           <div className="flex gap-2">
//             <BlueButton
//               onClick={() => navigate("/add-agent")}
//               className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
//               style={{ backgroundColor: "#0B5ED7" }}
//             >
//               <Plus size={14} className="mr-1" /> Add
//             </BlueButton>

//             <RefreshButton onClick={fetchAgents} />
//           </div>
//         </div>

//         {/* Loader */}
//         {loading && (
//           <div className="flex justify-center items-center h-[50vh]">
//             <RegularLoader />
//           </div>
//         )}

//         {/* No Records */}
//         {!loading && agents.length === 0 && (
//           <p className="text-center text-sm text-slate-500 py-3">
//             No matching agents found.
//           </p>
//         )}

//         {/* Table */}
//         {!loading && agents.length > 0 && (
//           <>
//             <div className="overflow-x-auto">
//               <table className="min-w-full text-sm text-center">
//                 <thead>
//                   <tr className="border-b border-slate-100 text-slate-500 px-2">
//                     <th className="py-2 ">Agent</th>
//                     <th className="py-2 ">Region</th>
//                     <th className="py-2 ">Contact</th>
//                     <th className="py-2 ">Retailers</th>
//                     <th className="py-2 ">Commission</th>
//                     <th className="py-2 ">Target</th>
//                     <th className="py-2 ">Status</th>
//                     <th className="py-2 ">Actions</th>
//                   </tr>
//                 </thead>

//                 <tbody className="px-2">
//                   {agents.map((a) => (
//                     <tr key={a.id} className="border-b border-slate-50 hover:bg-blue-50/60">

//                       {/* Agent */}
//                       <td className="py-1.5 text-xs">
//                         <div className="font-semibold">{a.agent_name}</div>
//                         <div className="text-[10px] text-slate-500">{a.agent_code}</div>
//                       </td>

//                       {/* Region */}
//                       <td className="py-1.5 text-[11px]">{a.city}, {a.state}</td>

//                       {/* Contact */}
//                       <td className="py-1.5 text-[11px]">
//                         <div>{a.phone}</div>
//                         <div className="text-[10px] text-slate-500">{a.email}</div>
//                       </td>

//                       {/* 🟦 Retailers column (hardcoded UI style) */}
//                       <td className="py-1.5 text-[11px]">
//                         <div>Onboarded: <strong>18</strong></div>
//                         <div className="text-[10px] text-slate-500">Active: 14</div>
//                       </td>

//                       {/* 🟦 Commission Column (hardcoded format like UI) */}
//                       <td className="py-1.5 text-[11px]">
//                         <div>Total: <strong>₹86,500</strong></div>
//                         <div className="text-[10px] text-slate-500">Pending: ₹12,500</div>
//                       </td>

//                       {/* 🟦 Target Column (progress bar UI like screenshot) */}
//                       <td className="py-1.5 text-[11px]">
//                         <div className="flex items-center justify-between text-[10px] mb-1">
//                           <span>Retailer target</span>
//                           <span>18/20 (90%)</span>
//                         </div>

//                         <div className="w-full h-2 rounded-full bg-slate-200">
//                           <div className="h-2 bg-blue-600 rounded-full" style={{ width: "90%" }}></div>
//                         </div>
//                       </td>

//                       {/* Status */}
//                       <td className="py-1.5">
//                         <span className={`px-2 py-1 rounded text-[10px] ${
//                           a.status === 1 
//                           ? "bg-green-50 text-green-700 border border-green-200"
//                           : "bg-red-50 text-red-700 border border-red-200"
//                         }`}>
//                           {a.status === 1 ? "Active" : "Inactive"}
//                         </span>
//                       </td>

//                       {/* Actions */}
//                       <td className="py-1.5 flex gap-1 justify-center">
//                         <button className="text-[11px] px-2 py-1 border border-blue-200 text-blue-700 bg-blue-50 rounded hover:bg-blue-100">
//                           View details
//                         </button>

//                         <button className="text-[10px] px-2 py-1 border border-slate-200 text-slate-700 bg-slate-50 rounded hover:bg-slate-100">
//                           Download statement
//                         </button>
//                       </td>

//                     </tr>
//                   ))}
//                 </tbody>

//               </table>
//             </div>

//             {/* Pagination */}
//             {totalPages > 1 && (
//               <Pagination
//                 currentPage={currentPage}
//                 totalPages={totalPages}
//                 onPageChange={setCurrentPage}
//               />
//             )}
//           </>
//         )}

//       </SectionCard>
//     </div>
//   );
// }



// import { Plus } from "lucide-react";
// import { useEffect, useState } from "react";
// import axios from "axios";
// import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import BlueButton from "../../../Component/Common/BlueButton";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { useNavigate } from "react-router-dom";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import Pagination from "../../../Component/Common/Pagination";

// export default function AgentsView() {

//   const navigate = useNavigate();
//   const [agents, setAgents] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);

//   // 🔹 Get company_id from session/local storage
//   const userData = JSON.parse(localStorage.getItem("authUserData")); // or "loginData" / "auth" depending on your key
//   const companyId = userData?.id;


//   const fetchAgents = async () => {
//     if (!companyId) return;

//     try {
//       setLoading(true);

//       const response = await axios.post(
//         "${BASE_URL}/agent/all",
//         { company_id: companyId, page: currentPage },
//         { headers: { "Content-Type": "application/json" } }
//       );

//       if (response.data?.status) {
//         setAgents(response.data.data);

//         // if backend gives pagination meta:
//         setTotalPages(response.data?.meta?.last_page || 1);
//       }
//     } catch (error) {
//       console.error(error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//   fetchAgents();
// }, [currentPage]);

//   return (
//     <div className="space-y-4">
//       <SectionCard title="Agent Performance & Commission">
//         <div className="flex justify-end gap-2">
//           <BlueButton
//             onClick={() => navigate("/add-agent")}
//             className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
//             style={{ backgroundColor: "#0B5ED7" }}
//           >
//             <Plus size={14} className="mr-1" /> Add
//           </BlueButton>

//           <RefreshButton onClick={fetchAgents} />
//         </div>

//         {/* Full screen loader */}
//         {loading && (
//           <div className="flex justify-center items-center h-[60vh]">
//             <RegularLoader />
//           </div>
//         )}

//         {/* No Data */}
//         {!loading && agents.length === 0 && (
//           <p className="text-center text-sm text-slate-500 py-3">No agents found.</p>
//         )}

//         {/* Table */}
//         {!loading && agents.length > 0 && (
//           <>
//             <div className="overflow-x-auto">
//               <table className="min-w-full text-xs">
//                 <thead>
//                   <tr className="border-b border-slate-100 text-slate-500">
//                     <th className="py-2 text-left">Agent</th>
//                     <th className="py-2 text-left">Region</th>
//                     <th className="py-2 text-left">Contact</th>
//                     <th className="py-2 text-left">Retailers</th>
//                     <th className="py-2 text-left">Commission</th>
//                     <th className="py-2 text-left">Target</th>
//                     <th className="py-2 text-left">Status</th>
//                     <th className="py-2 text-left">Actions</th>
//                   </tr>
//                 </thead>

//                 <tbody>
//                   {agents.map((a) => (
//                     <tr key={a.id} className="border-b border-slate-50 hover:bg-blue-50/60">
//                       <td className="py-1.5 text-xs text-slate-700">
//                         <div className="font-semibold">{a.agent_name}</div>
//                         <div className="text-[10px] text-slate-500">{a.agent_code}</div>
//                       </td>
//                       <td className="py-1.5 text-[11px] text-slate-700">
//                         {a.city}, {a.state}
//                       </td>
//                       <td className="py-1.5 text-[11px] text-slate-700">{a.phone}</td>
//                       <td className="py-1.5 text-[11px] text-slate-700">18 onboarded</td>
//                       <td className="py-1.5 text-[11px] text-slate-700">₹86,500</td>
//                       <td className="py-1.5 text-[11px] text-slate-700">80 target</td>

//                       <td className="py-1.5">
//                         <span className={`px-2 py-1 rounded text-[10px] ${
//                           a.status === 1 
//                           ? "bg-green-50 text-green-700 border border-green-200"
//                           : "bg-red-50 text-red-700 border border-red-200"
//                         }`}>
//                           {a.status === 1 ? "Active" : "Inactive"}
//                         </span>
//                       </td>

//                       <td className="py-1.5">
//                         <button className="text-[11px] px-2 py-1 border border-blue-200 text-blue-700 bg-blue-50 rounded hover:bg-blue-100 mr-1">
//                           View
//                         </button>
//                         <button className="text-[10px] px-2 py-1 border border-slate-200 text-slate-700 bg-slate-50 rounded hover:bg-slate-100">
//                           Download
//                         </button>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>

//             {/* Pagination */}
//             {totalPages > 1 && (
//               <Pagination
//                 currentPage={currentPage}
//                 totalPages={totalPages}
//                 onPageChange={setCurrentPage}
//               />
//             )}
//           </>
//         )}
//       </SectionCard>
//     </div>
//   );
// }






// import { Plus } from "lucide-react";
// import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import BlueButton from "../../../Component/Common/BlueButton";
// import { useNavigate } from "react-router-dom";
// import RefreshButton from "../../../Component/Common/RefreshButton";

// export default function AgentsView() {

//   const agents = [
//   {
//     id: 1,
//     name: "Rahul Patil",
//     code: "AGT-001",
//     region: "Pune & Nashik",
//     onboardedRetailers: 18,
//     activeRetailers: 14,
//     totalCommission: 86500,
//     pendingCommission: 12500,
//     targetRetailers: 20,
//     targetWarranties: 80,
//   },
//   {
//     id: 2,
//     name: "Priya Singh",
//     code: "AGT-002",
//     region: "Mumbai",
//     onboardedRetailers: 10,
//     activeRetailers: 9,
//     totalCommission: 64200,
//     pendingCommission: 8200,
//     targetRetailers: 15,
//     targetWarranties: 60,
//   },
// ];

// const navigate = useNavigate();

//   return (
//     <div className="space-y-4">
//       <SectionCard title="Agent Performance & Commission">

//         <div className="flex justify-end gap-2">
//           <BlueButton
//               onClick={() => navigate("/add-agent")}
//               className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
//               style={{ backgroundColor: "#0B5ED7" }}
//             >
//               <Plus className="mr-1" size={14} /> Add
//             </BlueButton>
//             <RefreshButton/>
//         </div>
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-xs">
//             <thead>
//               <tr className="border-b border-slate-100 text-slate-500">
//                 <th className="py-2 text-left">Agent</th>
//                 <th className="py-2 text-left">Region</th>
//                 <th className="py-2 text-left">Retailers</th>
//                 <th className="py-2 text-left">Commission (₹)</th>
//                 <th className="py-2 text-left">Targets</th>
//                 <th className="py-2 text-left">Actions</th>
//               </tr>
//             </thead>
//             <tbody>
//               {agents.map((a) => {
//                 const retailerProgress = Math.round(
//                   (a.onboardedRetailers / a.targetRetailers) * 100
//                 );
//                 return (
//                   <tr
//                     key={a.id}
//                     className="border-b border-slate-50 bg-white hover:bg-blue-50/60"
//                   >
//                     <td className="py-1.5 text-xs text-slate-700">
//                       <div className="font-semibold">{a.name}</div>
//                       <div className="text-[10px] text-slate-500">{a.code}</div>
//                     </td>
//                     <td className="py-1.5 text-[11px] text-slate-700">{a.region}</td>
//                     <td className="py-1.5 text-[11px] text-slate-700">
//                       <div>Onboarded: {a.onboardedRetailers}</div>
//                       <div className="text-[10px] text-slate-500">
//                         Active: {a.activeRetailers}
//                       </div>
//                     </td>
//                     <td className="py-1.5 text-[11px] text-slate-700">
//                       <div>Total: ₹ {formatCurrency(a.totalCommission)}</div>
//                       <div className="text-[10px] text-slate-500">
//                         Pending: ₹ {formatCurrency(a.pendingCommission)}
//                       </div>
//                     </td>
//                     <td className="py-1.5 text-[11px] text-slate-700">
//                       <div className="flex items-center justify-between">
//                         <span className="text-[10px] text-slate-500">
//                           Retailer target
//                         </span>
//                         <span className="text-[10px] font-semibold text-slate-700">
//                           {a.onboardedRetailers}/{a.targetRetailers} ({retailerProgress}% )
//                         </span>
//                       </div>
//                       <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1 overflow-hidden">
//                         <div
//                           className="h-1.5 bg-blue-500 rounded-full"
//                           style={{ width: `${Math.min(retailerProgress, 100)}%` }}
//                         />
//                       </div>
//                     </td>
//                     <td className="py-1.5 text-[11px] text-slate-700">
//                       <button className="text-[11px] px-2 py-1 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 mr-1">
//                         View details
//                       </button>
//                       <button className="text-[10px] px-2 py-1 rounded-lg border border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100">
//                         Download statement
//                       </button>
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         </div>
//       </SectionCard>

//       <SectionCard title="How Agent Dashboard is Integrated">
//         <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
//           <li>
//             Retailer onboarding flows with KYC, business details and assignment to
//             operations.
//           </li>
//           <li>
//             Commission reports per week/month, retailer-wise earnings and
//             consolidated statements.
//           </li>
//           <li>
//             Payout tracking: whether payable amount is transferred, with bank
//             account last 4 digits, transaction ID and date.
//           </li>
//           <li>
//             Targets set for onboarding and warranty sales per agent and tracked from
//             this admin view.
//           </li>
//         </ul>
//       </SectionCard>
//     </div>
//   );
// }